import Checkmark20 from '@carbon/icons-react/es/checkmark/20';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { faCommentsAlt } from '@fortawesome/pro-duotone-svg-icons';
import { WebViewerInstance } from '@pdftron/webviewer';
import { Dropdown } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FileAttachment } from 'api/attachments';
import { Discussion as IDiscussion } from 'api/document/types/document';
import { Menu } from 'components/atoms/dropdown';
import { EmptyState, EmptyStateIcon } from 'components/atoms/empty-state';
import { AttachmentDiscussion } from 'components/organisms/image-preview/attachment-discussion';
import { Trigger } from 'components/organisms/image-preview/comparison-footer';
import { useUser } from 'hooks/use-user';
import { useGetDiscussionsForAttachment } from 'queries/attachments';
import {
  CommentsHeader,
  CommentsSectionWrapper,
  CommentsText,
  CommonDiv,
  EmptyStateContainer,
  HideResolvedText,
  MenuItem,
  ShowResolvedText,
  StyledEmpty,
} from './comparison-styled-components';

interface ComparisonCommentProps {
  versionFileList?: FileAttachment[];
  leftAttachment: FileAttachment;
  rightAttachment: FileAttachment;
  selectedInsatnce: WebViewerInstance | null;
  discussionAttachmentId?: number;
  setDiscussionAttachmentId: (value: number) => void;
  showResolvedComments: boolean;
  toggleShowResolvedComments: () => void;
}

export const ComparisonComments: FC<ComparisonCommentProps> = ({
  versionFileList,
  leftAttachment,
  rightAttachment,
  selectedInsatnce,
  discussionAttachmentId,
  setDiscussionAttachmentId,
  showResolvedComments,
  toggleShowResolvedComments,
}) => {
  const { user } = useUser();
  const [activeDiscussion, setActiveDiscussion] =
    useState<IDiscussion | null>(null);

  const { data: discussionsInAttachment } = useGetDiscussionsForAttachment(
    Number(discussionAttachmentId),
  );

  const filteredDicsussions = useMemo(() => {
    const preFilteredDiscussions = discussionsInAttachment?.discussions ?? [];

    return preFilteredDiscussions.filter((e) => {
      if (e.comments[0]?.private === true) {
        if (e.comments[0]?.user.userType === user.userType) {
          return true;
        }

        return false;
      }

      return true;
    });
  }, [discussionsInAttachment?.discussions, user.userType]);

  const discussions = useMemo(
    () =>
      filteredDicsussions.filter((e) => {
        if (e.commentThread.resolved && !showResolvedComments) {
          return false;
        }

        return true;
      }),
    [filteredDicsussions, showResolvedComments],
  );

  const resolvedDiscussions = useMemo(
    () =>
      filteredDicsussions.filter((e) => {
        if (e.commentThread.resolved) {
          return true;
        }

        return false;
      }),
    [filteredDicsussions],
  );

  const getSelectedVersion = useCallback(
    (attachmentsList: FileAttachment[], attachmentId: number) => {
      return attachmentsList
        .map((attach, index) => {
          if (attach.attachmentId === attachmentId) {
            return attachmentsList.length - index;
          }

          return null;
        })
        .filter((value) => value !== null)[0];
    },
    [],
  );

  useEffect(() => {
    if (activeDiscussion && discussionsInAttachment) {
      const activeDiscussionInDiscussions =
        discussionsInAttachment.discussions.find(
          (e) =>
            e.commentThread.threadId ===
            activeDiscussion.commentThread.threadId,
        );

      if (activeDiscussionInDiscussions) {
        setActiveDiscussion(activeDiscussionInDiscussions);
      } else {
        setActiveDiscussion(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discussionsInAttachment]);

  const renderOverlay = (fileList: FileAttachment[]) => {
    return (
      <Menu>
        {fileList.map((version, index) => (
          <>
            {version.isDeleted === 'false' &&
            (leftAttachment.attachmentId === version.attachmentId ||
              rightAttachment.attachmentId === version.attachmentId) ? (
              <MenuItem
                key={version.attachmentId}
                title={
                  <FormattedMessage
                    id="image_preview.version.no"
                    values={{ number: fileList.length - index }}
                  />
                }
                onClick={() => {
                  setDiscussionAttachmentId(version.attachmentId);
                }}
              >
                <div>
                  <FormattedMessage
                    id="image_preview.version.no"
                    values={{ number: fileList.length - index }}
                  />
                  {version.latestVersion ? (
                    <span>
                      &nbsp;(
                      <FormattedMessage id="image_preview.current" />)
                    </span>
                  ) : null}
                </div>
                {discussionAttachmentId === version.attachmentId && (
                  <Checkmark20 />
                )}
              </MenuItem>
            ) : null}
          </>
        ))}
      </Menu>
    );
  };

  return (
    <CommentsSectionWrapper>
      {versionFileList && discussionAttachmentId && (
        <CommentsHeader>
          <CommentsText>
            <FormattedMessage id="image_preview.showing.comments.for" />
          </CommentsText>
          <CommonDiv>
            <Dropdown
              trigger={['click']}
              overlay={renderOverlay(versionFileList)}
            >
              <Trigger>
                <span>
                  <FormattedMessage
                    id="image_preview.version.no"
                    values={{
                      number: getSelectedVersion(
                        versionFileList,
                        discussionAttachmentId,
                      ),
                    }}
                  />
                  {versionFileList.find(
                    (attach) => attach.attachmentId === discussionAttachmentId,
                  )?.latestVersion ? (
                    <span>
                      &nbsp;(
                      <FormattedMessage id="image_preview.current" />)
                    </span>
                  ) : null}
                </span>
                <ChevronDown16 />
              </Trigger>
            </Dropdown>
            {resolvedDiscussions.length > 0 && (
              <HideResolvedText onClick={() => toggleShowResolvedComments()}>
                {showResolvedComments ? (
                  <FormattedMessage id="image_preview.hide.resolved.comments" />
                ) : (
                  <FormattedMessage id="image_preview.show.resolved.comments" />
                )}
              </HideResolvedText>
            )}
          </CommonDiv>
        </CommentsHeader>
      )}
      {discussionsInAttachment && discussionAttachmentId ? (
        discussions.length > 0 ? (
          <div>
            {discussions?.map((discussion) => (
              <AttachmentDiscussion
                activeDiscussion={activeDiscussion}
                attachmentId={discussionAttachmentId}
                discussion={discussion}
                instance={selectedInsatnce}
                projectId={leftAttachment.projectId}
                setActiveDiscussion={setActiveDiscussion}
                showResolvedComments={showResolvedComments}
              />
            ))}
          </div>
        ) : (
          <StyledEmpty>
            <EmptyStateContainer $transparent>
              <EmptyState
                image={<EmptyStateIcon icon={faCommentsAlt} />}
                title={
                  <FormattedMessage id="image_preview.comments.empty.title" />
                }
                message={
                  <FormattedMessage id="image_preview.comments.empty.message" />
                }
              />
              {resolvedDiscussions.length > 0 ? (
                <ShowResolvedText onClick={() => toggleShowResolvedComments()}>
                  {showResolvedComments ? (
                    <FormattedMessage id="image_preview.hide.resolved.comments" />
                  ) : (
                    <FormattedMessage id="image_preview.show.resolved.comments" />
                  )}
                </ShowResolvedText>
              ) : null}
            </EmptyStateContainer>
          </StyledEmpty>
        )
      ) : null}
    </CommentsSectionWrapper>
  );
};
